import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchApi } from "@/lib/api";
import { setReloadHold } from "@/lib/reload-holds";
import { encodeBase64Utf8, KORY_CONTENT_ENCODING } from "@/lib/content-encoding";
import { streamSSE } from "@/lib/sse";
import { openSearchRequest } from "@/lib/kory-open-search";
import { useCommandMenuRequest } from "@/contexts/command-menu-context";
import { freshReply, nounRefs, referFromReply, referTodos, releaseReferencedTodos } from "@/components/todos/tray/use-referenced-todos";
import { liveCardFromStream } from "@/components/todos/tray/live-cards";
import { loadNextAsks, nextAsksOf, nextAsksVersion, showNextAsks } from "@/components/todos/tray/next-asks";

import type { BriefRow, CosSessionMessage, CosSessionState, SuggestionRating } from "./types";

export const COS_SESSION_QUERY_KEY = ["kory", "cos", "session"] as const;
const REPLYING_POLL_MS = 4_000;
const IDLE_POLL_MS = 60_000;
const BUSY_RETRY_MS = 3_000;
const BUSY_RETRY_LIMIT = 40;
const KICKOFF_RETRY_LIMIT = 3;

/** The session's read: the flag, the session, today's brief, and whether Kory is replying anywhere.
 *  With the session off there is nothing to watch, so it reads once and stops polling. */
export function useCosSession() {
  return useQuery({
    queryKey: COS_SESSION_QUERY_KEY,
    queryFn: async () => (await fetchApi("/kory/cos/session")) as CosSessionState,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && !data.enabled) return false;
      return data?.replying ? REPLYING_POLL_MS : IDLE_POLL_MS;
    },
  });
}

/** The session's conversation, only while the session is on. */
export function sessionConversationId(state: CosSessionState | undefined): string | null {
  return state?.enabled ? state.conversation_id : null;
}

type HistoryResponse = { messages: CosSessionMessage[] };

/** Wrap a tray action ("Approved: …") so the transcript renders it as the user's action bubble. */
export function userActionText(text: string): string {
  return `<user-action>${text}</user-action>`;
}

/**
 * The one persistent session, rendered here from our own log (history) plus the live stream.
 * Sends are serialized server-side; a "busy" answer means another surface holds the turn, so
 * the message waits and resends with the same idempotency key.
 */
export function useCosSessionThread(state: CosSessionState | undefined, { kickoff: sendsKickoff = true } = {}) {
  const queryClient = useQueryClient();
  const conversationId = sessionConversationId(state);
  const briefDate = state?.brief_date ?? "";
  const [messages, setMessages] = useState<CosSessionMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  // A question still sending or waiting behind a busy session lives only in send()'s closure, so it
  // holds the version reload until it lands. Taken synchronously in send(): an effect would register
  // after the reload check in a commit that also navigates (the tray sends, then moves). Released only
  // in send()'s finally, never on unmount: the send keeps running after its tray unmounts.
  const holdId = useId();
  const openSearch = useCommandMenuRequest();
  // Every send still out, so Stop ends all of them: one waiting its turn too, not just the latest.
  const abortsRef = useRef(new Set<AbortController>());
  const kickoffSentRef = useRef<string | null>(null);
  // A history read that a send overtook would erase the live turn; the send reloads when it ends.
  const sendSeqRef = useRef(0);
  const inFlightRef = useRef(0);
  // The first history read only baselines the cards Kory named, unless the reply is fresh; a later reply marks them.
  const baselinedRef = useRef<string | null>(null);

  // ``marked``: the reload after this surface's send, whose reply already marked its cards as it streamed.
  const load = useCallback(async (marked: boolean) => {
    if (!conversationId) return;
    const seq = sendSeqRef.current;
    const asksAt = nextAsksVersion();
    // The tray renders no approval card: history leaves a late pause to the next send.
    const history = (await fetchApi(
      `/kory/conversations/${conversationId}/history?confirmation_cards=false`,
    )) as HistoryResponse;
    if (seq !== sendSeqRef.current || inFlightRef.current > 0) return;
    const loaded = history.messages ?? [];
    const reply = [...loaded].reverse().find((m) => m.role === "assistant");
    if (reply) referFromReply(reply, marked || (baselinedRef.current !== conversationId && !freshReply(reply)));
    baselinedRef.current = conversationId;
    loadNextAsks(conversationId, nextAsksOf(loaded, briefDate), asksAt);
    setMessages(loaded);
  }, [conversationId, briefDate]);
  const reload = useCallback(() => load(false), [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const send = useCallback(
    /** Resolves true once the turn streamed; false when it failed or stayed queued behind a busy session. */
    async (
      text: string,
      /** `context` is model-only (a `<page-context>` block): sent, never shown in the bubble. */
      opts: { hidden?: boolean; action?: boolean; kickoffToken?: string; context?: string } = {},
    ): Promise<boolean> => {
      if (!conversationId || !text.trim()) return false;
      const key = crypto.randomUUID();
      const said = opts.action ? userActionText(text) : text;
      const content = opts.context ? `${opts.context}\n\n${said}` : said;
      const assistantId = `live-${key}`;
      setMessages((prev) => [
        ...prev,
        ...(opts.hidden
          ? []
          : [{ id: `user-${key}`, role: "user" as const, content: text, kind: opts.action ? ("action" as const) : null }]),
        { id: assistantId, role: "assistant", content: "", streaming: true, kind: opts.hidden ? "brief" : null },
      ]);
      setSending(true);
      // Next asks are replaced every turn: the last reply's go as this one starts.
      showNextAsks([], conversationId);
      sendSeqRef.current += 1;
      const sendSeq = sendSeqRef.current;
      inFlightRef.current += 1;
      setReloadHold(holdId, true);
      let delivered = false;
      // The turn never started (the session could not be set up): history has no reply to show.
      let failed = false;
      let replied = false;
      // The stream marked this reply's cards; otherwise (none named, or the event never arrived) its history does.
      let named = false;
      const controller = new AbortController();
      abortsRef.current.add(controller);
      try {
        for (let attempt = 0; attempt < BUSY_RETRY_LIMIT; attempt += 1) {
          let busy = false;
          for await (const event of streamSSE(
            "/kory/stream",
            {
              messages: [{ role: "user", content: encodeBase64Utf8(content) }],
              content_encoding: KORY_CONTENT_ENCODING,
              conversation_id: conversationId,
              idempotency_key: key,
              kickoff_token: opts.kickoffToken,
              // No approval card here: a pause an earlier relay gave up on is interrupted, never resumed.
              confirmation_cards: false,
            },
            controller.signal,
          )) {
            if (event.type === "busy") busy = true;
            // A proposal shows in the tray as a skeleton, then as its card (which this reply names).
            if (liveCardFromStream(event)) named = true;
            // Cumulative per reply: the latest event replaces the last. An empty, released one: Kory moved on.
            if (event.type === "referenced_todos" && event.released) releaseReferencedTodos();
            else if (event.type === "referenced_todos" && event.todo_ids) {
              named = true;
              referTodos(event.todo_ids, assistantId, event.sections, nounRefs(event.nouns), event.order);
            }
            // Only the latest send's reply opens anything: not one they stopped, nor one they sent past.
            if (event.type === "open_search" && !controller.signal.aborted && sendSeq === sendSeqRef.current) {
              const request = openSearchRequest(event);
              if (request) openSearch?.(request);
            }
            if (event.type === "next_asks") {
              const asks = event.asks ?? [];
              showNextAsks(asks, conversationId);
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, nextAsks: asks } : m)));
            }
            if (event.type === "error" && !replied) {
              failed = true;
              const reason = event.content || "Kory couldn't start. Try again.";
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: reason, streaming: false } : m)),
              );
            }
            if (event.type === "working" && event.content) {
              const note = event.content;
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, working: note } : m)));
            }
            if (event.type === "text" && event.content) {
              replied = true;
              const chunk = event.content;
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m)),
              );
            }
          }
          if (!busy) {
            delivered = !failed;
            break;
          }
          setQueued(true);
          await new Promise((resolve) => setTimeout(resolve, BUSY_RETRY_MS));
          // A Stop while it waited its turn ends it; it never sends behind the reply you stopped.
          if (controller.signal.aborted) break;
        }
      } catch {
        delivered = false;
      } finally {
        abortsRef.current.delete(controller);
        inFlightRef.current -= 1;
        setReloadHold(holdId, inFlightRef.current > 0);
        setQueued(false);
        setSending(false);
        // A reload would replace the error with a history that has no reply.
        if (!failed) await load(named);
        void queryClient.invalidateQueries({ queryKey: COS_SESSION_QUERY_KEY });
      }
      return delivered;
    },
    [conversationId, queryClient, load, openSearch, holdId],
  );

  const interrupt = useCallback(async () => {
    for (const controller of abortsRef.current) controller.abort();
    await fetchApi("/kory/cos/session/interrupt", { method: "POST" });
    void queryClient.invalidateQueries({ queryKey: COS_SESSION_QUERY_KEY });
  }, [queryClient]);

  // The day's brief: sent once, hidden, the first time this card sees an undelivered kickoff.
  // A send that fails or never gets the session is forgotten and retried a poll later, a few
  // times: an unchanged refresh carries the same kickoff and token, so nothing else re-sends it.
  // Only the server's token claims the day; the tag alone is an ordinary message.
  // Surfaces other than the Overview card (the global tray) never send it.
  const kickoff = sendsKickoff ? (state?.kickoff ?? null) : null;
  const kickoffToken = state?.kickoff_token ?? null;
  // Retries are counted per brief, so a new day's brief starts with all of them.
  const [kickoffRetry, setKickoffRetry] = useState<{ kickoff: string; n: number } | null>(null);
  const kickoffRetryTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(kickoffRetryTimerRef.current), []);
  useEffect(() => {
    if (!kickoff || !kickoffToken || !conversationId || kickoffSentRef.current === kickoff) return;
    clearTimeout(kickoffRetryTimerRef.current);
    kickoffSentRef.current = kickoff;
    const retries = kickoffRetry?.kickoff === kickoff ? kickoffRetry.n : 0;
    void send(kickoff, { hidden: true, kickoffToken }).then((delivered) => {
      if (delivered || kickoffSentRef.current !== kickoff) return;
      kickoffSentRef.current = null;
      if (retries >= KICKOFF_RETRY_LIMIT) return;
      kickoffRetryTimerRef.current = setTimeout(
        () => setKickoffRetry({ kickoff, n: retries + 1 }),
        IDLE_POLL_MS,
      );
    });
  }, [conversationId, kickoff, kickoffToken, send, kickoffRetry]);

  return { messages, send, interrupt, sending, queued, reload };
}

/** One-tap Useful / Obvious / Wrong on a Kory proposal (by row id), or a suggested sales step's Wrong step (by its
 *  to-do key), stored on its row. */
export function useRateSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ todoId, rating }: { todoId: string; rating: SuggestionRating }) =>
      fetchApi(`/kory/cos/suggestions/${encodeURIComponent(todoId)}/rating`, { method: "POST", body: JSON.stringify({ rating }) }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: COS_SESSION_QUERY_KEY }),
  });
}

/** The executor's action id for a CoS proposal row (`cos:{date}:{action id}`), else null. */
export function actionIdOf(row: BriefRow): string | null {
  const parts = row.todo_key.split(":");
  return parts[0] === "cos" && parts.length >= 3 ? parts.slice(2).join(":") : null;
}
